import React, { useState, useEffect } from 'react';
import { Button, Modal, MarkdownRenderer } from '../common';
import { calculatePercentOriginal, formatPercentOriginal } from '../../utils/textSimilarity';

/**
 * Modal for editing text blocks (GM text or player prose)
 */
export const GMTextEditor = ({ isOpen, block, onSave, onCancel }) => {
  const [content, setContent] = useState(block?.content || '');
  const [previewMode, setPreviewMode] = useState(false);

  useEffect(() => {
    if (isOpen && block) {
      setContent(block.content || '');
      setPreviewMode(false);
    }
  }, [isOpen, block]);

  const handleSave = () => {
    onSave({
      ...block,
      content: content.trim()
    });
  };

  const isEditing = block?.content?.length > 0;
  const isPlayerContent = block?.type === 'player_content';
  // Legacy support: check if it's an old transition block
  const isLegacyTransition = block?.type === 'gm_transition';

  // Calculate % original for player content
  const percentOriginal = isPlayerContent && block?.originalContent
    ? calculatePercentOriginal(block.originalContent, content)
    : null;

  const getTitle = () => {
    if (isPlayerContent) {
      return isEditing ? 'Edit Player Text' : 'Edit Player Text';
    }
    return isEditing ? 'Edit Text' : 'Add Text';
  };

  const getDescription = () => {
    if (isPlayerContent) {
      return 'Edit the player\'s submission. Changes are tracked and displayed.';
    }
    return 'Add your own narrative text to the passage.';
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      title={getTitle()}
      size="md"
    >
      <p className="text-sm text-gray-500 mb-2">
        {getDescription()}
      </p>

      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <p className="text-xs text-gray-500">
            Supports <a href="https://www.markdownguide.org/basic-syntax/" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">Markdown</a>
          </p>
          {percentOriginal !== null && percentOriginal < 100 && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
              {formatPercentOriginal(percentOriginal) || 'edited'}
            </span>
          )}
        </div>
        <div className="flex border border-gray-300 rounded-lg overflow-hidden text-sm">
          <button
            type="button"
            className={`px-3 py-1 transition-colors ${
              !previewMode
                ? 'bg-primary-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
            onClick={() => setPreviewMode(false)}
          >
            Write
          </button>
          <button
            type="button"
            className={`px-3 py-1 transition-colors ${
              previewMode
                ? 'bg-primary-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
            onClick={() => setPreviewMode(true)}
          >
            Preview
          </button>
        </div>
      </div>

      {previewMode ? (
        <div className="w-full border rounded-lg p-3 text-sm overflow-auto bg-gray-50 min-h-[200px]">
          {content ? (
            <div className={isLegacyTransition ? 'text-gray-600 italic' : ''}>
              <MarkdownRenderer content={content} />
            </div>
          ) : (
            <p className="text-gray-400 italic">Nothing to preview yet...</p>
          )}
        </div>
      ) : (
        <textarea
          className="w-full border rounded-lg p-3 text-sm resize-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          rows={8}
          placeholder="The court fell silent as..."
          value={content}
          onChange={(e) => setContent(e.target.value)}
          autoFocus
        />
      )}

      {/* Show original content for reference when editing player prose */}
      {isPlayerContent && block?.originalContent && (
        <details className="mt-3">
          <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
            View original submission
          </summary>
          <div className="mt-2 p-3 bg-gray-50 rounded-lg text-sm text-gray-600 max-h-32 overflow-y-auto">
            <MarkdownRenderer content={block.originalContent} />
          </div>
        </details>
      )}

      <div className="flex justify-end gap-2 mt-4">
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={!content.trim()}>
          {isEditing ? 'Update' : 'Add to Passage'}
        </Button>
      </div>
    </Modal>
  );
};
